import { useState } from "react";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";
import { Logo } from "../components/ui/Logo";
import { LogoDark } from "../components/ui/LogoDark";

export function LoginScreen({ onLogin, onSignup }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState(null); // null | 'success' | 'error'
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const submitReset = async () => {
    if (!resetEmail) { setResetError("Required"); return; }
    if (!/\S+@\S+\.\S+/.test(resetEmail)) { setResetError("Enter a valid email"); return; }
    setResetError("");
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, { redirectTo: "https://opencall.com.au/reset-password" });
      if (error) throw error;
      setResetStatus("success");
    } catch (err) {
      setResetError(err.message);
      setResetStatus("error");
    } finally {
      setResetLoading(false);
    }
  };

  const submit = async () => {
    const e = {};
    if (!email) e.email = "Required";
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = "Enter a valid email";
    if (!password) e.password = "Required";
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      // Fetch profile to get user_type
      const { data: profileData } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', data.user.id)
        .single();
      onLogin(profileData?.user_type || 'artist');
    } catch (err) {
      setErrors({ general: err.message });
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <div className="auth-visual">
        <div><Logo/></div>
        <div style={{ position:"relative",zIndex:1 }}>
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.35rem",color:"#f0e2cc",fontWeight:300,lineHeight:1.5,marginBottom:14,fontStyle:"italic" }}>
            "opencall. completely changed how I find exhibition opportunities."
          </div>
          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".68rem",color:C.clay,letterSpacing:"1.5px",textTransform:"uppercase" }}>Yindi Nguyen</div>
          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".6rem",color:"rgba(196,168,130,.4)",marginTop:3 }}>Painter & Printmaker · Naarm / Melbourne</div>
        </div>
        <div style={{ display:"flex",gap:24 }}>
          {[["320+","Open calls"],["2,400+","Artists"],["190+","Galleries"]].map(([n,l])=>(
            <div key={l}><div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.3rem",color:C.clay }}>{n}</div><div style={{ fontFamily:"'DM Mono',monospace",fontSize:".56rem",color:"rgba(196,168,130,.4)",letterSpacing:"1.5px",textTransform:"uppercase",marginTop:2 }}>{l}</div></div>
          ))}
        </div>
      </div>
      <div className="auth-panel">
        <div style={{ maxWidth:400,width:"100%" }}>
          <div style={{ marginBottom:28 }}><LogoDark/></div>
          <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.9rem",fontWeight:400,color:C.bark,marginBottom:6 }}>Welcome back</h1>
          <p style={{ fontSize:".85rem",color:C.muted,marginBottom:28,lineHeight:1.65 }}>Log in to your opencall. account.</p>
          {errors.general && (
            <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"10px 14px",marginBottom:16,fontSize:".8rem",color:C.rose }}>⚠ {errors.general}</div>
          )}
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className={`form-input ${errors.email?"err":""}`} type="email" placeholder="your@email.com" value={email} onChange={e=>{setEmail(e.target.value);setErrors({});}} onKeyDown={e=>e.key==="Enter"&&submit()}/>
            {errors.email&&<div style={{ fontSize:".72rem",color:C.rose,marginTop:4 }}>⚠ {errors.email}</div>}
          </div>
          <div className="form-group">
            <div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}>
              <label className="form-label" style={{ margin:0 }}>Password</label>
              <button onClick={()=>{setShowReset(!showReset);setResetStatus(null);setResetError("");}} style={{ background:"none",border:"none",color:C.clay,fontSize:".72rem",cursor:"pointer" }}>Forgot password?</button>
            </div>
            <div style={{ position:"relative" }}>
              <input className={`form-input ${errors.password?"err":""}`} type={showPw?"text":"password"} placeholder="Your password" value={password} onChange={e=>{setPassword(e.target.value);setErrors({});}} onKeyDown={e=>e.key==="Enter"&&submit()}/>
              <button onClick={()=>setShowPw(!showPw)} style={{ position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.muted,minHeight:36,minWidth:36,display:"flex",alignItems:"center",justifyContent:"center" }}>{showPw?"🙈":"👁️"}</button>
            </div>
            {errors.password&&<div style={{ fontSize:".72rem",color:C.rose,marginTop:4 }}>⚠ {errors.password}</div>}
          </div>
          {showReset&&(
            <div style={{ marginTop:16,padding:"16px",background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8 }}>
              <p style={{ fontSize:".8rem",color:C.muted,marginBottom:12,lineHeight:1.6 }}>Enter your email and we'll send you a reset link.</p>
              {resetStatus==="success"?(
                <div style={{ background:"#e6f4ea",border:"1px solid #a8d5b0",borderRadius:5,padding:"10px 14px",fontSize:".8rem",color:"#2e7d40" }}>Check your email for a reset link.</div>
              ):(
                <>
                  <div className="form-group" style={{ marginBottom:10 }}>
                    <input className={`form-input${resetError?" err":""}`} type="email" placeholder="your@email.com" value={resetEmail} onChange={e=>{setResetEmail(e.target.value);setResetError("");}} onKeyDown={e=>e.key==="Enter"&&submitReset()}/>
                    {resetError&&<div style={{ fontSize:".72rem",color:C.rose,marginTop:4 }}>⚠ {resetError}</div>}
                  </div>
                  <button className="btn-g" style={{ width:"100%" }} onClick={submitReset} disabled={resetLoading}>{resetLoading?"Sending…":"Send reset link"}</button>
                </>
              )}
            </div>
          )}
          <button className="btn-p" style={{ width:"100%",marginTop:8 }} onClick={submit} disabled={loading}>
            {loading?"Logging in…":"Log In →"}
          </button>
          <div style={{ marginTop:20,textAlign:"center",fontSize:".8rem",color:C.muted }}>
            Don't have an account?{" "}
            <button onClick={onSignup} style={{ background:"none",border:"none",color:C.clay,fontWeight:500,cursor:"pointer",fontSize:".8rem",textDecoration:"underline" }}>Create one free</button>
          </div>
        </div>
      </div>
    </div>
  );
}
